import dayjs from "dayjs";
import "dayjs/locale/ko";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-chained-backend";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import { SUPPORTED_LANGUAGES, TRANSLATION_NAMESPACES } from "~/shared/constants/i18n";
import {
  systemFormatCalendarYear,
  systemFormatCalendarYearMonth,
  systemFormatDate,
  systemFormatDateSeries,
  systemFormatNumber,
  systemFormatNumberCompact,
  systemFormatTimeDuration,
  systemFormatTimeDurationDistribution,
} from "./format";
import { checkIsValidEnum } from "./validate";

const FORMATTERS = {
  BASIC_NUMBER: "format__basic_number",
  BASIC_NUMBER_COMPACT: "format__basic_number_compact",
  NUMBER_COUNT: "format__number_count",
  NUMBER_COUNT_COMPACT: "format__number_count_compact",
  NUMBER_COUNT_RAW: "format__number_count_raw",
  NUMBER_COUNT_COMPACT_RAW: "format__number_count_compact_raw",
  PERCENT: "format__percent",
  PERCENT_COMPACT: "format__percent_compact",
  PERSON: "format__person",
  PERSON_COMPACT: "format__person_compact",
  MONTH: "format__month",
  CALENDAR_YEAR_MONTH: "format__calendar_year_month",
  CALENDAR_YEAR: "format__calendar_year",
  HOUR: "format__hour",
  DATE: "format__date",
  TIME_DURATION: "format__time_duration",
  DATE_SERIES: "format__date_series", // 시작 날짜부터 값을 더해가며 날짜를 표현
  TIME_DURATION_DISTRIBUTION: "format__time_duration_distribution",
  BOOLEAN_NUMBER_VALUE: "format__boolean_number_value",
  MEMBER_COUNT: (value: number | string) => {
    value = Math.min(+value, 5);
    if (value < 1 || isNaN(value)) return "-";
    return i18n.t(`format__member_count_${value}`);
  },
  GENDER: (value: string | number) =>
    checkIsValidEnum(+value, 1) ? `format__gender_${+value}` : "-",
  GENDER_COMPACT: (value: string | number) => {
    return checkIsValidEnum(+value, 1) ? `format__gender_compact_${+value}` : "-";
  },
  AGE: (value: string | number) => (checkIsValidEnum(+value, 6) ? `format__age_${+value}` : "-"),
  IS_VISITED: (value: boolean) => `format__is_visited_${value}`,
  WEEKDAY: (value: string | number) => {
    return checkIsValidEnum(+value, 6) ? `format__weekday_${+value}` : "-";
  },
  WEEKDAY_COMPACT: (value: string | number) => {
    return checkIsValidEnum(+value, 6) ? `format__weekday_compact_${+value}` : "-";
  },
} as const;

export const initializeI18n = () => {
  i18n
    .use(LanguageDetector)
    .use(Backend)
    .use(initReactI18next)
    .init({
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: "ko",
      ns: TRANSLATION_NAMESPACES,
      defaultNS: "translation",
      detection: {
        // querystring(?language=) 이 항상 우선 — 공유 링크의 명시 언어 보존.
        // 없으면 localStorage 캐시로 폴백 → 내부 이동/새로고침/북마크 시 언어 유지.
        order: ["querystring", "localStorage"],
        lookupQuerystring: "language",
        lookupLocalStorage: "language",
        caches: ["localStorage"],
      },
      interpolation: {
        escapeValue: false,
      },
      backend: {
        backends: [HttpBackend, HttpBackend],
        backendOptions: [
          // kenny: mST locale
          {
            loadPath: "/locales/{{lng}}/{{ns}}.json",
            crossDomain: false,
            // 배포마다 바뀌는 ?v=<빌드ID> 로 브라우저 캐시 무력화 → 새 번역 키 즉시 반영.
            // (locale JSON 은 URL 이 고정이라 이 쿼리가 없으면 stale 캐시가 남는다)
            queryStringParams: { v: import.meta.env.VITE_BUILD_ID },
          },
          // kenny: mash-design-system locale
          {
            loadPath: "https://locale.mash-board.io/api/v1/locale/{{lng}}",
            crossDomain: true,
          },
        ],
      },
      debug: import.meta.env.VITE_NODE_ENV === "development",
    });

  i18n.on("languageChanged", (lang) => {
    document.documentElement.lang = lang;
    dayjs.locale(lang);
  });

  i18n.services.formatter?.add(FORMATTERS.BASIC_NUMBER, systemFormatNumber);
  i18n.services.formatter?.add(FORMATTERS.BASIC_NUMBER_COMPACT, systemFormatNumberCompact);
  i18n.services.formatter?.add(FORMATTERS.MONTH, (value: number, lng?: string) => {
    return dayjs()
      .month(value)
      .locale(lng ?? "en")
      .format("MMM");
  });
  i18n.services.formatter?.add(FORMATTERS.HOUR, (value: number | string, lng = "en") => {
    const hour = typeof value === "string" ? (value.split(":").at(0) ?? 0) : value;
    return dayjs().locale(lng).hour(+hour).format("H");
  });
  i18n.services.formatter?.add(FORMATTERS.DATE, systemFormatDate);
  i18n.services.formatter?.add(FORMATTERS.TIME_DURATION, systemFormatTimeDuration);
  i18n.services.formatter?.add(
    FORMATTERS.PERCENT,
    (value: number, lng?: string, options?: Intl.NumberFormatOptions) => {
      return systemFormatNumber(value * 100, lng, options);
    }
  );
  i18n.services.formatter?.add(
    FORMATTERS.PERCENT_COMPACT,
    (value: number, lng?: string, options?: Intl.NumberFormatOptions) => {
      return systemFormatNumberCompact(value * 100, lng, options);
    }
  );

  i18n.services.formatter?.add(FORMATTERS.DATE_SERIES, systemFormatDateSeries);
  i18n.services.formatter?.add(
    FORMATTERS.TIME_DURATION_DISTRIBUTION,
    systemFormatTimeDurationDistribution
  );
  i18n.services.formatter?.add(FORMATTERS.CALENDAR_YEAR_MONTH, systemFormatCalendarYearMonth);
  i18n.services.formatter?.add(FORMATTERS.CALENDAR_YEAR, systemFormatCalendarYear);
  i18n.services.formatter?.add(
    FORMATTERS.BOOLEAN_NUMBER_VALUE,
    (value: number | string | boolean) => {
      value = typeof value === "string" ? +value : value;
      value = typeof value === "boolean" ? value : value > 0;
      return value ? "O" : "X";
    }
  );

  window.i18n = {
    instance: i18n,
    formatters: FORMATTERS,
  };
};
